// src/config/propertySetupSchemaConfig.ts
//
// Schema-driven definition of the "Another Home – Property Setup Onboarding Guide".
//
// The single source of truth is `propertySetupSchema.json`: an ordered list of the
// exact `${anchor}` placeholders in the real Word template, each tagged with the
// section it belongs to, its type ("checkbox" | "text") and a (sometimes rough)
// label extracted from the docx.
//
// This module:
//   - loads & types that schema,
//   - groups it by section for rendering,
//   - derives a readable display label per anchor (never mutating the `key`),
//   - and provides helpers to go between the flat `anchors` map (what the docx PDF
//     generator consumes) and the legacy `{sections:[...]}` structure (what the HTML
//     preview + admin panel still read).
//
// IMPORTANT: `key` values are the docx anchors and must NEVER be changed here.

import rawSchema from './propertySetupSchema.json';
import {
    PropertySetup,
    PropertySetupAnchors,
    PropertySetupSchemaEntry,
    PropertySetupSection,
    PropertySetupItem,
} from '../types';

// JSON import is typed as a broad array; assert the concrete schema entry shape.
export const PROPERTY_SETUP_SCHEMA: PropertySetupSchemaEntry[] =
    rawSchema as unknown as PropertySetupSchemaEntry[];

// A section as rendered by the form: the section name + its ordered anchors.
export interface PropertySetupSchemaSection {
    section: string;
    entries: PropertySetupSchemaEntry[];
}

// Optional intro paragraph shown under a section heading.
export const SECTION_INTROS: Record<string, string> = {
    'Linen & Towel Requirements':
        'Guests receive starter supplies only. Keep adequate labeled inventory for turnovers.',
    'Kitchen Inventory & Essentials':
        'For each appliance, indicate whether it is available and note the brand / model where relevant.',
    'Additional Amenities Checklist':
        'For each amenity, mark whether it is available and add location / details where useful.',
};

// Curated, human-readable labels keyed by the docx anchor `key`.
// Derived from the section context + the original onboarding guide so the form reads
// well even though many raw docx labels are empty or truncated. Keys are untouched.
const LABEL_OVERRIDES: Record<string, string> = {
    // 1. Compliance & Administrative Requirements
    cb_confirmThatHomeownerInsurance: 'Homeowner insurance includes short-term rental coverage',
    text: 'Rental permit number',
    cb_bankingDetailsForPayout: 'Banking details ready for payout setup (can be completed together on a call)',

    // 2. Booking Preferences & Availability
    text2: 'Desired listing launch date',
    text3: 'Owner block-out dates',
    cb_3Months6Months: 'Booking window: 3 months',
    cb_6Months12Months: 'Booking window: 6 months',
    cb_12MonthsOther: 'Booking window: 12 months',
    cb_other: 'Booking window: other',
    text4: 'Booking window: other (please specify)',
    text5: 'Minimum stay (nights)',
    cb_30Days60Days: 'Maximum stay: 30 days',
    cb_60Days90Days: 'Maximum stay: 60 days',
    cb_90DaysOther: 'Maximum stay: 90 days',
    cb_other2: 'Maximum stay: other',
    text6: 'Maximum stay: other (please specify)',

    // 3. Property Information
    text7: 'Maximum guest occupancy',
    text8: 'Bedroom 1 - bed type / configuration',
    text9: 'Bedroom 2 - bed type / configuration',
    text10: 'Bedroom 3 - bed type / configuration',
    text11: 'Bedroom 4 - bed type / configuration',
    text12: 'Bedroom 5 - bed type / configuration',
    text13: 'Bedroom 6 - bed type / configuration',
    text14: 'Bedroom 7 - bed type / configuration',
    text15: 'Number of bathrooms',
    text16: 'Approx. square footage',
    cb_yesNoAccessInstructions: 'Garage available — Yes',
    cb_noAccessInstructionsCode: 'Garage available — No',
    text17: 'Garage access instructions / code',
    cb_guestAccessAllowedNo: 'Basement guest access allowed — Yes',
    cb_noGuestAccessNotes: 'Basement guest access — No',
    text18: 'Basement access notes',

    // 4. Guest Access & Connectivity
    cb_yesNoEntryCode: 'Smart lock installed — Yes',
    cb_noEntryCode: 'Smart lock installed — No',
    text19: 'Smart lock entry code',
    cb_yesNoLocation: 'Backup lockbox available — Yes',
    cb_noLocation: 'Backup lockbox available — No',
    text20: 'Lockbox location',
    text21: 'Wi-Fi name (SSID)',
    text22: 'Wi-Fi password',
    text23: 'Modem / router location',
    cb_yesNoExtendersYes: 'Mesh network — Yes',
    cb_noExtendersYesNo: 'Mesh network — No',
    cb_yesNoHotspotYes: 'Wi-Fi extenders — Yes',
    cb_noHotspotYesNo: 'Wi-Fi extenders — No',
    cb_yesNo: 'Hotspot — Yes',
    cb_no: 'Hotspot — No',

    // 5. Linen & Towel Requirements
    cb_twoCompleteWhiteSheet: 'Two complete white sheet sets per bedroom, clearly labeled and stored in clear containers',
    cb_bathTowelSetsSufficient: 'Bath towel sets sufficient for maximum guest occupancy',
    cb_blackMakeupTowelsRecommended: 'Black makeup towels (recommended: one per bathroom)',
    cb_poolTowelsRecommendedTwo: 'Pool towels (recommended: two per guest)',
    cb_extraBlanketsAndPillows: 'Extra blankets and pillows in clearly labeled storage',

    // 6. Bathroom Amenities
    cb_shampooConditionerAndBody: 'Shampoo, conditioner, and body wash / shower gel (consistent product line in every bathroom)',
    cb_toiletPaperStarterSupply: 'Toilet paper starter supply',
    cb_hairDryerInEach: 'Hair dryer in each bathroom',

    // 7. Kitchen Inventory & Essentials
    cb_dishwasherPodsDishSoap: 'Dishwasher pods, dish soap, hand soap, and kitchen sponges',
    cb_paperTowelsAndNapkins: 'Paper towels and napkins',
    cb_coffeeAndTeaStarter: 'Coffee and tea starter supply (Keurig preferred where practical)',
    cb_trashBagsRecyclingBags: 'Trash bags, recycling bags, foil, and parchment paper',
    cb_yesNo2: 'Coffee maker available — Yes',
    cb_no2: 'Coffee maker available — No',
    text24: 'Coffee maker - brand / notes',
    cb_yesNo3: 'Dishwasher available — Yes',
    cb_no3: 'Dishwasher available — No',
    text25: 'Dishwasher - brand / notes',
    cb_yesNo4: 'Microwave available — Yes',
    cb_no4: 'Microwave available — No',
    text26: 'Microwave - brand / notes',
    cb_yesNo5: 'Toaster available — Yes',
    cb_no5: 'Toaster available — No',
    text27: 'Toaster - brand / notes',
    cb_yesNo6: 'Oven available — Yes',
    cb_no6: 'Oven available — No',
    text28: 'Oven - brand / notes',
    cb_yesNo7: 'Blender available — Yes',
    cb_no7: 'Blender available — No',
    text29: 'Blender - brand / notes',
    cb_yesNo8: 'Kettle available — Yes',
    cb_no8: 'Kettle available — No',
    text30: 'Kettle - brand / notes',
    text31: 'Coffee machine - brand / model',
    text32: 'Coffee machine - pod type',
    cb_dinnerwareGlasswareSilverwareAnd: 'Dinnerware, glassware, silverware, and cooking utensils',
    cb_redAndWhiteWine: 'Red and white wine glasses, serving platters, and salad bowl',
    cb_sharpKnifeSetAnd: 'Sharp knife set and cutting boards',
    cb_basicPantryStaplesSalt: 'Basic pantry staples: salt, pepper, olive oil, and sugar',

    // 8. Cleaning Products
    cb_laundryDetergentBleachDisinfectant: 'Laundry detergent, bleach, disinfectant spray, and window cleaner',
    cb_bathroomAndKitchenCleaning: 'Bathroom and kitchen cleaning products',
    cb_grillCleaner: 'Grill cleaner',
    text33: 'Vacuum - brand / model',
    text34: 'Vacuum - instructions',
    cb_basicMultipurposeKitchenAnd: 'Basic multipurpose, kitchen, and bathroom cleaning supplies (guest)',
    cb_disinfectingSpray: 'Disinfecting spray (guest)',
    cb_broomAndDustpanMop: 'Broom and dustpan, mop, and guest-accessible vacuum',
    cb_spareVacuumBagsOr: 'Spare vacuum bags or filters, if needed',
    cb_ironAndIroningBoard: 'Iron and ironing board',

    // 9. Additional Amenities Checklist (Yes / No + details, per amenity)
    cb_box: 'Ceiling fans — Yes',
    cb_box2: 'Ceiling fans — No',
    text35: 'Ceiling fans - details / location',
    cb_box3: 'Laundry hampers — Yes',
    cb_box4: 'Laundry hampers — No',
    text36: 'Laundry hampers - details / location',
    cb_box5: 'Smart TVs — Yes',
    cb_box6: 'Smart TVs — No',
    text37: 'Smart TVs - details / location',
    cb_box7: 'Blackout curtains / shades — Yes',
    cb_box8: 'Blackout curtains / shades — No',
    text38: 'Blackout curtains / shades - details / location',
    cb_box9: 'Mosquito nets — Yes',
    cb_box10: 'Mosquito nets — No',
    text39: 'Mosquito nets - details / location',
    cb_box11: 'Books / board games — Yes',
    cb_box12: 'Books / board games — No',
    text40: 'Books / board games - details / location',
    cb_box13: "Children's toys — Yes",
    cb_box14: "Children's toys — No",
    text41: "Children's toys - details / location",
    cb_box15: 'Crib — Yes',
    cb_box16: 'Crib — No',
    text42: 'Crib - details / location',
    cb_box17: 'High chair — Yes',
    cb_box18: 'High chair — No',
    text43: 'High chair - details / location',
    cb_box19: 'Luggage racks — Yes',
    cb_box20: 'Luggage racks — No',
    text44: 'Luggage racks - details / location',
    cb_box21: 'Full-length mirrors — Yes',
    cb_box22: 'Full-length mirrors — No',
    text45: 'Full-length mirrors - details / location',
    cb_box23: 'Indoor fireplace — Yes',
    cb_box24: 'Indoor fireplace — No',
    text46: 'Indoor fireplace - details / location',
    text47: 'Fireplace notes (usage instructions / wood supply / safety notes)',

    // 10. Home Systems & Maintenance
    cb_confirmThatHeatingAnd: 'Heating and air-conditioning systems fully operational',
    cb_installLocksOnClosets: 'Locks installed on closets used to store personal belongings',
    text48: 'Thermostats - quantity',
    text49: 'Thermostats - instructions',
    text50: 'Trash service - pickup day / time',
    text51: 'Trash service - bin location / rules',
    cb_timedNotTimedSchedule: 'Sprinkler system — Timed',
    cb_notTimedSchedule: 'Sprinkler system — Not timed',
    text52: 'Sprinkler system - schedule',
    text53: 'Pool service - frequency and schedule',
    text54: 'Landscaping service - frequency and schedule',
    text55: 'Preferred vendor - plumber (contact / company)',
    text56: 'Preferred vendor - electrician (contact / company)',
    text57: 'Preferred vendor - HVAC (contact / company)',
    text58: 'Preferred vendor - pool service (contact / company)',
    text59: 'Preferred vendor - landscaping (contact / company)',
    text60: 'Cleaning team contact',
    text61: 'Smart TV instructions (guest logins and remote-control notes)',

    // 11. Kitchen & Laundry Instructions
    text62: 'Dishwasher - usage and special instructions',
    text63: 'Refrigerator / freezer - ice maker, features, or operational notes',
    text64: 'Washer & dryer - location / model / usage instructions',

    // 12. Living Room, Furniture & Home Care
    cb_furnitureShouldNotBe: 'Furniture should not be moved or rearranged by guests',
    cb_identifyDelicateFurnishingsValuable: 'Delicate furnishings, valuable pieces, or items requiring special cleaning identified',
    text65: 'Special care notes',

    // 13. BBQ, Pool & Outdoor Setup
    cb_yesNoLocation2: 'BBQ grill available — Yes',
    cb_noLocation2: 'BBQ grill available — No',
    text66: 'BBQ grill - location',
    text67: 'BBQ - usage instructions',
    text68: 'BBQ - propane refill process',
    text69: 'BBQ - utensil / grill brush location',
    text70: 'BBQ - grill cover storage',
    text71: 'BBQ - lighter location',
    cb_yesNoSaltwaterYes: 'Pool heated — Yes',
    cb_noSaltwaterYesNo: 'Pool heated — No',
    cb_yesNo9: 'Pool saltwater — Yes',
    cb_no9: 'Pool saltwater — No',
    text72: 'Pool lighting - switch location and instructions',
    cb_noGlassNearThe: 'No glass near the pool',
    cb_noPetsOrFood: 'No pets or food in or immediately around the pool',
    cb_followAllPostedSafety: 'Follow all posted pool safety rules',
    text73: 'Outdoor - dining tables / chairs',
    text74: 'Outdoor - lounge chairs',
    text75: 'Outdoor - umbrellas',
    text76: 'Outdoor - furniture covers',
    text77: 'Outdoor - bug control solutions',
    cb_yesNoTowelHooks: 'Outdoor shower — Yes',
    cb_noTowelHooksRack: 'Outdoor shower — No',
    cb_yesNo10: 'Outdoor towel hooks / rack — Yes',
    cb_no10: 'Outdoor towel hooks / rack — No',

    // 14. Safety & Security
    text78: 'Fire extinguisher location',
    text79: 'Fire blanket location',
    text80: 'First-aid kit location',
    cb_yesNoCoAlarms: 'Smoke alarms operational — Yes',
    cb_noCoAlarmsYes: 'Smoke alarms operational — No',
    cb_yesNo11: 'CO alarms operational — Yes',
    cb_no11: 'CO alarms operational — No',
    cb_yesNoPerimeterAnd: 'Exterior cameras (perimeter & entry points only) — Yes',
    cb_noPerimeterAndEntry: 'Exterior cameras (perimeter & entry points only) — No',
    cb_yesNoDrivewayWalkway: 'Motion lighting — Yes',
    cb_noDrivewayWalkwayDetails: 'Motion lighting — No',
    text81: 'Motion lighting - driveway / walkway details',

    // 15. Guest Rules & Property Policies
    text82: 'Quiet hours',
    text83: 'Visitor / day guest policy',
};

// Optional helper hints shown under specific fields.
export const FIELD_HINTS: Record<string, string> = {
    text3: 'Example: June 16-22, 2026',
    text5: 'We recommend no fewer than 2 nights.',
    text20: 'Please provide a photo of the lockbox location when possible.',
};

// Strips docx template artifacts from a raw label (`${...}` fragments, stray leading
// braces, collapsed whitespace). Returns '' when nothing meaningful remains or the
// label is just a Yes/No/Other cluster that carries no real meaning on its own.
function cleanRawLabel(raw: string): string {
    if (!raw) return '';
    let out = raw.replace(/\$\{[^}]*\}?/g, ' '); // drop ${...} template fragments
    out = out.replace(/[{}$]/g, ' '); // drop stray braces / dollar signs
    out = out.replace(/\s+/g, ' ').trim();
    out = out.replace(/[:;,\s]+$/g, '').trim(); // trim trailing punctuation
    if (!out) return '';
    if (/^(yes\s+no|no|yes|other)$/i.test(out)) return '';
    return out;
}

// Prettifies a docx anchor key as a last-resort label (e.g. `cb_ceilingFans` ->
// "Ceiling fans", `text12` -> "Text 12"). Only reached when there is no override
// and no usable raw label.
function prettifyKey(key: string): string {
    const base = key.replace(/^cb_/, '');
    const spaced = base
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Best display label for a schema entry, never touching its `key`.
export function deriveLabel(entry: PropertySetupSchemaEntry): string {
    const override = LABEL_OVERRIDES[entry.key];
    if (override) return override;
    const cleaned = cleanRawLabel(entry.label);
    if (cleaned) return cleaned;
    return prettifyKey(entry.key);
}

// The schema grouped by section (first-seen order preserved), used to render the form.
export const PROPERTY_SETUP_SCHEMA_SECTIONS: PropertySetupSchemaSection[] = (() => {
    const order: string[] = [];
    const map = new Map<string, PropertySetupSchemaEntry[]>();
    for (const entry of PROPERTY_SETUP_SCHEMA) {
        if (!map.has(entry.section)) {
            map.set(entry.section, []);
            order.push(entry.section);
        }
        map.get(entry.section)!.push(entry);
    }
    return order.map((section) => ({ section, entries: map.get(section)! }));
})();

// The default value for an anchor: false for checkboxes, '' for text fields.
function emptyValueFor(entry: PropertySetupSchemaEntry): string | boolean {
    return entry.type === 'checkbox' ? false : '';
}

// Builds a fresh anchors map with every schema key present at its empty value.
export function buildEmptyAnchors(): PropertySetupAnchors {
    const anchors: PropertySetupAnchors = {};
    for (const entry of PROPERTY_SETUP_SCHEMA) {
        anchors[entry.key] = emptyValueFor(entry);
    }
    return anchors;
}

// Prefills an anchors map from previously saved data. Starts from the empty schema
// (so new/removed anchors are reconciled) and overlays saved values, coercing each
// to the type the schema declares. Reads `propertySetup.anchors` when present.
export function anchorsFromPropertySetup(saved: PropertySetup | null | undefined): PropertySetupAnchors {
    const anchors = buildEmptyAnchors();
    const savedAnchors = saved?.anchors;
    if (!savedAnchors || typeof savedAnchors !== 'object') return anchors;
    for (const entry of PROPERTY_SETUP_SCHEMA) {
        if (!(entry.key in savedAnchors)) continue;
        const value = savedAnchors[entry.key];
        anchors[entry.key] =
            entry.type === 'checkbox' ? Boolean(value) : value == null ? '' : String(value);
    }
    return anchors;
}

// Rebuilds the legacy `{sections:[{title,items:[{label,value,type}]}]}` structure from
// an anchors map, so the HTML preview and admin panel keep working unchanged.
export function buildSections(anchors: PropertySetupAnchors): PropertySetupSection[] {
    return PROPERTY_SETUP_SCHEMA_SECTIONS.map<PropertySetupSection>((section) => ({
        title: section.section,
        items: section.entries.map<PropertySetupItem>((entry) => {
            const raw = anchors[entry.key];
            const value: string | boolean =
                entry.type === 'checkbox' ? Boolean(raw) : raw == null ? '' : String(raw);
            return { label: deriveLabel(entry), value, type: entry.type };
        }),
    }));
}

// The full PropertySetup payload sent to the backend: the flat `anchors` map (for the
// docx PDF generator) PLUS the rebuilt `sections` (for the legacy HTML preview path).
export function buildPropertySetupPayload(anchors: PropertySetupAnchors): PropertySetup {
    return { anchors, sections: buildSections(anchors) };
}
